import { Input } from "@digipost/dds-input-react";
import { useLanguage } from "@digipost/translate";
import { faExclamationCircle } from "@fortawesome/pro-solid-svg-icons/faExclamationCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, useState } from "react";

import {
  SiteImproveActions,
  SiteImproveCategories,
  siteImproveEvent,
} from "../analytics/SiteImprove";
import { useTranslate } from "../infrastructure/useTranslate";
import styles from "./ContactFormPerson.module.css";
import countryCodesEn from "./country-codes/en.json";
import countryCodesNb from "./country-codes/nb.json";
import countryCodesNn from "./country-codes/nn.json";

const languageToCountryCodes = {
  nb: countryCodesNb,
  nn: countryCodesNn,
  en: countryCodesEn,
};

type Props = {
  onChange: (
    changes: Partial<
      Record<"countryCode" | "epost" | "nationalNumber", string>
    >,
  ) => void;
  epost?: string;
  phoneLandCode?: string;
  phone?: string;
  epostValid: boolean;
  phoneValid: boolean;
};

export const ContactFormPerson: FC<Props> = ({
  onChange,
  epost,
  phoneLandCode,
  phone,
  epostValid,
  phoneValid,
}) => {
  const { language } = useLanguage();
  const translate = useTranslate();
  const [touchedEpost, setTouchedEpost] = useState<boolean>(false);
  const [touchedCountry, setTouchedCountry] = useState<boolean>(false);
  const [touchedNumber, setTouchedNumber] = useState<boolean>(false);

  const trackTouchEpost = (): void => {
    if (!touchedEpost) {
      siteImproveEvent({
        category: SiteImproveCategories.PERSON_REGISTRATION,
        action: SiteImproveActions.WRITE_EPOST_INPUT,
        label: "/person/skriver",
      });

      setTouchedEpost(true);
    }
  };

  const trackTouchCountryCode = (): void => {
    if (!touchedCountry) {
      siteImproveEvent({
        category: SiteImproveCategories.PERSON_REGISTRATION,
        action: SiteImproveActions.WRITE_COUNTRY_CODE_INPUT,
        label: "/person/skriver",
      });
      setTouchedCountry(true);
    }
  };

  const trackTouchNumber = (): void => {
    if (!touchedNumber) {
      siteImproveEvent({
        category: SiteImproveCategories.PERSON_REGISTRATION,
        action: SiteImproveActions.WRITE_PHONE_INPUT,
        label: "/person/skriver",
      });
      setTouchedNumber(true);
    }
  };

  const isMobileView = window.innerWidth <= 1050;

  return (
    <div className={styles.wrapper}>
      <Input
        labelText={translate("register.your_email")}
        autoComplete={"email"}
        errorText={
          !epostValid
            ? translate("register.org.registration_form.invalid_email")
            : undefined
        }
        errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
        onChange={(event) => {
          trackTouchEpost();
          onChange({ epost: event.target.value });
        }}
        valid={epostValid}
        value={epost}
        name="email"
        data-testid="person-email-input"
      />
      <div className={styles.phoneNumberSection}>
        <div className={styles.countryCode}>
          <label className={styles.countryCodeLabel} htmlFor={"country-code"}>
            {translate("register.your_phone_number_country_code")}
          </label>
          <label
            className={styles.numberInputLabelMobile}
            htmlFor={"country-code"}
          >
            {translate("register.your_phone_number")}
          </label>
          <select
            className={styles.select}
            name="country-code"
            id={"country-code"}
            value={phoneLandCode}
            autoComplete="tel-country-code"
            data-testid="person-phone-land-code-input"
            onChange={(event) => {
              trackTouchCountryCode();
              onChange({ countryCode: event.target.value });
            }}
          >
            {languageToCountryCodes[language].map(({ code, country }, i) => (
              <option value={`+${code}`} key={i}>{`${
                isMobileView ? "" : country
              } (+${code})`}</option>
            ))}
          </select>
        </div>
        <div className={styles.numberInput}>
          <Input
            labelText={translate("register.your_phone_number")}
            labelClass={styles.numberInputLabel}
            errorText={
              !phoneValid
                ? translate("register.org.registration_form.invalid_phone")
                : undefined
            }
            errorIcon={<FontAwesomeIcon icon={faExclamationCircle} />}
            onChange={(event) => {
              trackTouchNumber();
              onChange({ nationalNumber: event.target.value });
            }}
            valid={phoneValid}
            value={phone}
            name="phone"
            data-testid="person-phone-number-input"
            autoComplete="tel-national"
          />
        </div>
      </div>
    </div>
  );
};
